import { clsx } from 'clsx';
import { format } from 'date-fns';
import { bool, func, string } from 'prop-types';
import { useContext, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { BenefitsContext, DatePicker, FormInput, FormSelect, Loader, Modal, RadioGroup } from 'components';
import { addUuid, getValidDate } from 'helpers';
import { useCivility, useFetch, useSafeState, useUser } from 'hooks';

const RightHolderModal = ({ isModalOpen, setIsModalOpen, mode = '' }) => {
  const { rightHolder, rightHoldersData, setRightHolders } = useContext(BenefitsContext);
  const { currentUser } = useUser();

  const { t } = useTranslation();
  const { call: deleteRightHolderCall, loading: deleteLoading } = useFetch();
  const { call: addRightHolderCall, loading: addLoading } = useFetch();
  const { call: editRightHolderCall, loading: editLoading } = useFetch();
  const [isEditable, setIsEditable] = useSafeState(true);
  const { civilityOptions } = useCivility();

  const taxationOptions = useMemo(
    () =>
      addUuid(
        [
          { name: 'taxation', value: 'Commune', label: t('form:taxationOptions.common') },
          { name: 'taxation', value: 'Séparé', label: t('form:taxationOptions.separated') }
        ],
        'input-taxation-radio-'
      ),
    []
  );

  const linkTypeOptions = useMemo(
    () =>
      addUuid(
        [
          { value: 'Enfant', label: 'Enfant' },
          { value: 'Marié(e)', label: 'Marié(e)' },
          { value: 'Célibataire', label: 'Célibataire' },
          { value: 'Concubin(e)', label: 'Concubin(e)' },
          { value: 'Pacsé(e)', label: 'Pacsé(e)' }
        ],
        'select-linktype-option-'
      ),
    []
  );

  const { control, register, setValue, handleSubmit, reset, getValues, formState } = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      name: '',
      birthDate: null,
      address1: '',
      civility: '',
      postalCode: '',
      city: '',
      taxation: ''
    }
  });

  const providerValues = { reset, control, register, setValue, handleSubmit, getValues, formState };

  useEffect(() => {
    onToggleModal();
  }, [rightHolder, mode]);

  const onSubmit = async (newUserData) => {
    newUserData = {
      ...newUserData,
      birthDate: format(newUserData.birthDate, 'dd/MM/yyyy'),
      linkType: newUserData.linkType.value
    };

    if (mode === 'edit') {
      setIsEditable(false);

      newUserData = {
        ...newUserData,
        address2: '',
        address3: '',
        sameAddress: true,
        type: 'Domicile'
      };

      const { data } =
        (await editRightHolderCall({
          service: 'castelis',
          method: 'put',
          path: `/users/addresses/${rightHolder.serialNumber}`,
          query: newUserData
        })) || {};

      if (data?.status === 200) {
        const updatedRightHolders = rightHoldersData.rightHolders.map((holder) => {
          if (holder.serialNumber === rightHolder.serialNumber) {
            return { ...holder, ...newUserData };
          }
          return holder;
        });

        setRightHolders((prev) => ({ ...prev, rightHolders: updatedRightHolders }));
        setIsModalOpen(false);
        toast.success(t('dashboard:editRightHolderSuccess'));
      }
    } else if (mode === 'add') {
      newUserData = {
        ...newUserData,
        taxation: newUserData.taxation.value
      };

      const { data } =
        (await addRightHolderCall({
          service: 'castelis',
          method: 'post',
          path: `/users/rightHolders/${currentUser.serialNumber}/rightHolder/`,
          query: newUserData
        })) || {};

      if (data?.status === 200) {
        newUserData.status = t('dashboard:status.pending');
        setRightHolders((prev) => ({ ...prev, rightHolders: [...prev.rightHolders, newUserData] }));
        onToggleModal(true);
        toast.success(t('dashboard:addRightHolderSuccess'));
      }
    }
  };

  const setFieldData = () => {
    setValue('firstName', rightHolder?.firstName, { shouldValidate: true });
    setValue('name', rightHolder?.name, { shouldValidate: true });
    setValue('birthDate', new Date(getValidDate(rightHolder?.birthDate)), { shouldValidate: true });
    setValue('address1', rightHolder?.address1 ?? rightHolder?.address, { shouldValidate: true });
    setValue('city', rightHolder?.city, { shouldValidate: true });
    setValue('postalCode', rightHolder?.postalCode, { shouldValidate: true });
    setValue(
      'linkType',
      linkTypeOptions.find((option) => option.value === rightHolder?.linkType),
      { shouldValidate: true }
    );
    setValue(
      'taxation',
      taxationOptions.find((option) => option.value === rightHolder?.taxation),
      { shouldValidate: true }
    );
    setValue('civility', rightHolder?.civility === 'Mme' ? civilityOptions?.[1]?.value : civilityOptions?.[0]?.value, {
      shouldValidate: true
    });
  };

  const deleteRightHolder = async () => {
    const { data } =
      (await deleteRightHolderCall({
        service: 'castelis',
        method: 'delete',
        path: `/users/rightHolders/${currentUser.serialNumber}/rightHolder/${rightHolder?.serialNumber}`
      })) || {};

    if (data?.status === 200) {
      const updatedRightHolders = rightHoldersData.rightHolders.filter(
        (holder) => holder.serialNumber !== rightHolder.serialNumber
      );
      setRightHolders((prev) => ({ ...prev, rightHolders: updatedRightHolders }));
      setIsModalOpen(false);
      toast.success(t('dashboard:deleteRightHolderSucess'));
    }
  };

  const onToggleModal = (closeModal = false) => {
    if (mode === 'edit') {
      rightHolder && setFieldData();
      setIsEditable(false);
    } else {
      reset();
      setIsEditable(true);
    }
    closeModal && setIsModalOpen(false);
  };

  switch (mode) {
    case 'add':
    case 'edit':
      return (
        <Modal
          isOpen={isModalOpen}
          size="medium"
          header={
            <h2 className="h2 underlined-yellow">
              {mode === 'add' ? t('dashboard:addRightHolder') : t('dashboard:editRightHolder')}
            </h2>
          }
          body={
            <FormProvider {...providerValues}>
              <form onSubmit={handleSubmit(onSubmit)} className="rightholder-form">
                <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
                  <p className={clsx('label col-span-4', !isEditable && 'disabled')}>
                    {t('form:linkType')}&nbsp;<span className="text-primary-500 ml-1">*</span>
                  </p>
                  <Controller
                    render={({ field: { onChange } }) => (
                      <FormSelect
                        options={linkTypeOptions}
                        onChange={onChange}
                        disabled={!isEditable}
                        className={clsx('col-span-8', !isEditable && 'disable')}
                        name="linkType"
                      />
                    )}
                    name="linkType"
                    control={control}
                    rules={{ required: mode === 'add' }}
                  />
                </div>
                <div className={clsx('grid grid-cols-12 w-full gap-4 items-center mb-5', mode === 'edit' && 'hidden')}>
                  <p className={clsx('label col-span-4', !isEditable && 'disabled')}>
                    {t('form:taxation')}&nbsp;<span className="text-primary-500 ml-1">*</span>
                  </p>
                  <Controller
                    render={({ field: { onChange } }) => (
                      <FormSelect
                        options={taxationOptions}
                        onChange={onChange}
                        disabled={!isEditable}
                        className={clsx('col-span-8', !isEditable && 'disable')}
                        name="taxation"
                      />
                    )}
                    name="taxation"
                    control={control}
                    rules={{ required: mode === 'add' }}
                  />
                </div>
                <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
                  <p className={clsx('label col-span-4', !isEditable && 'disabled')}>
                    {t('form:lastname')} {t('form:firstname')}&nbsp;<span className="text-primary-500 ml-1">*</span>
                  </p>
                  <FormInput
                    name="name"
                    rules={{ required: mode === 'add' }}
                    placeholder={t('form:lastname')}
                    disabled={!isEditable}
                    className={clsx('col-span-4', !isEditable && 'disabled')}
                  />
                  <FormInput
                    name="firstName"
                    placeholder={t('form:firstname')}
                    disabled={!isEditable}
                    className={clsx('col-span-4', !isEditable && 'disabled')}
                    rules={{ required: mode === 'add' }}
                  />
                </div>
                <div className="grid grid-cols-12">
                  <p className={clsx('label col-span-4', !isEditable && 'disabled')}>
                    {t('form:civility.label')}&nbsp;<span className="text-primary-500 ml-1">*</span>
                  </p>
                  <RadioGroup
                    radioOptions={civilityOptions}
                    row
                    className="col-span-8 mb-3"
                    disabled={!isEditable}
                    rules={{ required: mode === 'add' }}
                  />
                </div>
                <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
                  <p className={clsx('label col-span-4', !isEditable && 'disabled')}>
                    {t('form:birthdate')}&nbsp;<span className="text-primary-500 ml-1">*</span>
                  </p>
                  <div className="col-span-8">
                    <Controller
                      control={control}
                      name="birthDate"
                      rules={{ required: mode === 'add' }}
                      render={({ field: { onChange, value } }) => (
                        <DatePicker
                          selected={value}
                          onChange={onChange}
                          name="birthDate"
                          placeholder="jj/mm/aaaa"
                          peekNextMonth
                          disabled={!isEditable}
                          showMonthDropdown
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={40}
                          openToDate={new Date('1970/01/01')}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
                  <p className={clsx('label col-span-4', !isEditable && 'disabled')}>
                    {t('form:address')}&nbsp;<span className="text-primary-500 ml-1">*</span>
                  </p>
                  <FormInput
                    name="address1"
                    placeholder={t('form:address')}
                    className="col-span-8"
                    rules={{ required: true }}
                  />
                </div>

                <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
                  <p className={clsx('label col-span-4', !isEditable && 'disabled')}>
                    {t('form:cityPostalCode')}&nbsp;<span className="text-primary-500 ml-1">*</span>
                  </p>

                  <FormInput
                    name="postalCode"
                    placeholder={t('form:postalCode')}
                    rules={{ required: true }}
                    className="col-span-4"
                  />
                  <FormInput
                    name="city"
                    placeholder={t('form:city')}
                    rules={{ required: true }}
                    className="col-span-4"
                  />
                </div>

                <div className="flex justify-end">
                  <button type="submit" disabled={!formState.isValid || addLoading || editLoading} className="btn">
                    {(addLoading || editLoading) && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
                    {t('buttons.continue')}
                  </button>
                </div>
              </form>
            </FormProvider>
          }
          onClose={() => onToggleModal(true)}
        />
      );
    case 'delete':
      return (
        <Modal
          isOpen={isModalOpen}
          size="medium"
          header={<h2 className="h2 underlined-yellow">{t('dashboard:benefits.titleRightHolderModal')}</h2>}
          footer={
            <div className="flex-center-between">
              <button type="button" className="btn outlined" onClick={() => setIsModalOpen(false)}>
                {t('buttons.goBack')}
              </button>

              <button type="button" disabled={deleteLoading} className="btn" onClick={deleteRightHolder}>
                {deleteLoading && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
                {t('buttons.confirm')}
              </button>
            </div>
          }
          body={
            <div className="flex flex-col gap-6">
              <p> {t('dashboard:benefits.deleteRightHolder')} </p>
              <ul>
                <li>
                  {rightHolder?.firstName} {rightHolder?.name}
                </li>
                <li>{rightHolder?.birthDate}</li>
                <li>{rightHolder?.linkType}</li>
                <li>{rightHolder?.address1}</li>
              </ul>
            </div>
          }
          onClose={() => onToggleModal(true)}
        />
      );
  }
};

RightHolderModal.propTypes = {
  isModalOpen: bool.isRequired,
  setIsModalOpen: func.isRequired,
  mode: string.isRequired
};

export default RightHolderModal;
